import { slugify } from './export/download';

/**
 * File System Access API integration (Chromium desktop). Lets markbird open a
 * local `.md` file, edit it, and save back in place — complementing the
 * always-available import/export. Feature-detected; unsupported browsers
 * (Firefox/Safari) simply don't surface these commands and keep using
 * import/download.
 *
 * File handles are remembered per document for the session so "Save to file"
 * writes back to the same file without re-prompting.
 */
const handles = new Map<string, FileSystemFileHandle>();

const MD_TYPES: FilePickerAcceptType[] = [
  {
    description: 'Markdown',
    accept: {
      'text/markdown': ['.md', '.markdown', '.mdown', '.mkd'],
      'text/plain': ['.txt'],
    },
  },
];

export function supportsFileSystemAccess(): boolean {
  return typeof window !== 'undefined' && 'showOpenFilePicker' in window;
}

export interface OpenedFile {
  name: string;
  content: string;
  handle: FileSystemFileHandle;
}

/** Prompt to open a local markdown file. Returns null if cancelled/unsupported. */
export async function openMarkdownFromDisk(): Promise<OpenedFile | null> {
  const pick = window.showOpenFilePicker;
  if (!pick) return null;
  try {
    const [handle] = await pick({ types: MD_TYPES, multiple: false });
    if (!handle) return null;
    const file = await handle.getFile();
    return { name: file.name, content: await file.text(), handle };
  } catch {
    return null; // user cancelled
  }
}

export function rememberHandle(docId: string, handle: FileSystemFileHandle): void {
  handles.set(docId, handle);
}

export function hasHandle(docId: string): boolean {
  return handles.has(docId);
}

async function ensureWritable(handle: FileSystemFileHandle): Promise<boolean> {
  const opts = { mode: 'readwrite' } as const;
  if ((await handle.queryPermission?.(opts)) === 'granted') return true;
  return (await handle.requestPermission?.(opts)) === 'granted';
}

/**
 * Write the doc to its linked file, or prompt Save As if none is linked (or
 * `forceNew`). Returns true when written, false on cancel/unsupported.
 */
export async function saveMarkdownToDisk(
  docId: string,
  title: string,
  content: string,
  forceNew = false,
): Promise<boolean> {
  const save = window.showSaveFilePicker;
  if (!save) return false;
  try {
    let handle = forceNew ? undefined : handles.get(docId);
    if (!handle) {
      handle = await save({
        suggestedName: `${slugify(title || 'untitled')}.md`,
        types: MD_TYPES,
      });
      handles.set(docId, handle);
    } else if (!(await ensureWritable(handle))) {
      return false;
    }
    const writable = await handle.createWritable();
    await writable.write(content);
    await writable.close();
    return true;
  } catch {
    return false; // user cancelled or permission denied
  }
}
